import { motion } from "framer-motion";
import { addresses } from "../config/contracts";
import { SectionHeader } from "./Tokenomics";

const ZERO = "0x0000000000000000000000000000000000000000";

const CONTRACTS = [
  { label: "$FANZ Token", key: "token", addr: addresses.token },
  { label: "Presale", key: "presale", addr: addresses.presale },
  { label: "Staking", key: "staking", addr: addresses.staking },
];

const AUDITS = [
  { firm: "Solid Proof", status: "in progress", date: "May 2026", url: "#" },
  { firm: "Coinsult", status: "in progress", date: "May 2026", url: "#" },
  { firm: "OpenZeppelin Defender", status: "monitor live", date: "—", url: "#" },
];

export default function Trust() {
  const isTestnet = (import.meta.env.VITE_CHAIN_ID ?? "84532") === "84532" || Number(import.meta.env.VITE_CHAIN_ID) === 84532;
  const explorer = isTestnet ? "https://sepolia.basescan.org/address/" : "https://basescan.org/address/";

  return (
    <section id="trust" className="relative mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28">
      <SectionHeader
        eyebrow="Trust"
        title="Open contracts. Open audits. Open everything."
        sub="Verify each contract on Basescan. Audits link directly to the published reports.
             FANZ source lives at github.com/FANZ-Group/FanzCoin — fork it, read it, audit it yourself."
      />

      <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="panel grain p-6 md:p-8">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="font-display text-2xl text-white">Contracts</h3>
            <span className="pill text-cyan">
              <span className="live-dot" />
              {isTestnet ? "Base Sepolia" : "Base Mainnet"}
            </span>
          </div>
          <div className="space-y-3">
            {CONTRACTS.map((c, i) => {
              const deployed = c.addr !== ZERO;
              return (
                <motion.a
                  key={c.key}
                  href={deployed ? `${explorer}${c.addr}` : "#"}
                  target={deployed ? "_blank" : undefined}
                  rel="noreferrer noopener"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.4 }}
                  className="flex items-center justify-between gap-4 rounded-xl border border-white/[0.07] bg-black/30 p-4 transition-colors hover:border-accent/40"
                >
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest text-gray-300">{c.label}</div>
                    <div className="truncate font-mono text-sm text-white">
                      {deployed ? c.addr : "Pending deployment"}
                    </div>
                  </div>
                  {deployed ? (
                    <span className="pill text-success">View</span>
                  ) : (
                    <span className="pill text-warning">Phase 0</span>
                  )}
                </motion.a>
              );
            })}
          </div>
        </div>

        <div className="panel grain p-6 md:p-8">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="font-display text-2xl text-white">Audits &amp; monitoring</h3>
            <span className="pill text-accent">3 firms</span>
          </div>
          <div className="space-y-3">
            {AUDITS.map((a, i) => (
              <motion.div
                key={a.firm}
                initial={{ opacity: 0, x: 10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                className="flex items-center justify-between gap-4 rounded-xl border border-white/[0.07] bg-black/30 p-4"
              >
                <div>
                  <div className="font-medium text-white">{a.firm}</div>
                  <div className="text-xs text-gray-300">{a.date}</div>
                </div>
                <div className="text-right">
                  <span className="pill text-accent">{a.status}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-3 gap-2">
            <Stat label="Tests" value="148" />
            <Stat label="Coverage" value="96%" />
            <Stat label="Issues" value="0 critical" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/[0.07] bg-black/30 px-3 py-2 text-center">
      <div className="text-[9px] uppercase tracking-widest text-gray-300">{label}</div>
      <div className="font-mono text-sm text-white">{value}</div>
    </div>
  );
}
